/**
 * Operator management — the people who can sign into this portal.
 *
 * Reachable only with `operator:read`, which only the Owner role holds, so in
 * practice this is the Owner screen. It is still built against the capability
 * model rather than against the role name: every control asks
 * `can('operator:write')`, so if the role model ever changes shape this screen
 * follows it without an edit.
 *
 * The role picker is populated from GET /operators/roles, which the backend
 * serves straight out of the module that enforces the roles. No role name is
 * spelled in this file.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../components/ui/table';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Loader2, Plus, Search, ShieldCheck, KeyRound, LogOut, UserCheck, UserX, Pencil } from 'lucide-react';
import { useAdminAuth } from '../auth/AdminAuthContext';
import { PermissionGate } from '../components';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin`;
const PAGE_SIZE = 25;

const WRITE = 'operator:write';
const ALL_FILTER = '__all__';

const emptyDraft = {
  email: '', password: '', first_name: '', last_name: '', roles: [],
};

const OperatorsPage = () => {
  const { getAdminToken, adminUser } = useAdminAuth();

  const [operators, setOperators] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState(ALL_FILTER);
  const [activeFilter, setActiveFilter] = useState(ALL_FILTER);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${getAdminToken()}` }),
    [getAdminToken],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
      if (search.trim()) params.search = search.trim();
      if (roleFilter !== ALL_FILTER) params.role = roleFilter;
      if (activeFilter !== ALL_FILTER) params.is_active = activeFilter === 'active';
      const { data } = await axios.get(`${API}/operators`, { headers, params });
      setOperators(data.operators || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load operators.');
    } finally {
      setLoading(false);
    }
  }, [headers, page, search, roleFilter, activeFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    axios.get(`${API}/operators/roles`, { headers })
      .then(({ data }) => setRoles(data.roles || []))
      // The picker is unusable without this, but the list still works, so this
      // surfaces where it matters rather than blanking the page.
      .catch(() => toast.error('Could not load the role catalogue.'));
  }, [headers]);

  const act = async (label, fn) => {
    setSaving(true);
    try {
      await fn();
      toast.success(label);
      await load();
      return true;
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : `${label} failed.`);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const createOperator = async () => {
    const okDone = await act('Team member added', () =>
      axios.post(`${API}/operators`, draft, { headers }));
    if (okDone) { setCreateOpen(false); setDraft(emptyDraft); }
  };

  const saveEdit = async () => {
    const okDone = await act('Team member updated', () =>
      axios.patch(`${API}/operators/${editing.id}`, {
        first_name: editing.first_name,
        last_name: editing.last_name,
        roles: editing.roles,
      }, { headers }));
    if (okDone) setEditing(null);
  };

  const resetPassword = async () => {
    const okDone = await act('Password set; sessions revoked', () =>
      axios.post(`${API}/operators/${resetting.id}/reset-password`,
        { password: newPassword }, { headers }));
    if (okDone) { setResetting(null); setNewPassword(''); }
  };

  const toggleActive = (op) => act(
    op.is_active ? 'Access revoked' : 'Access restored',
    () => axios.post(`${API}/operators/${op.id}/${op.is_active ? 'disable' : 'enable'}`, {}, { headers }),
  );

  const revokeSessions = (op) => act('Sessions revoked', () =>
    axios.post(`${API}/operators/${op.id}/revoke-sessions`, {}, { headers }));

  const toggleRole = (target, role) => {
    const has = target.roles.includes(role);
    return { ...target, roles: has ? target.roles.filter((r) => r !== role) : [...target.roles, role] };
  };

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const isSelf = (op) => op.id === adminUser?.id;

  const RolePicker = ({ value, onChange }) => (
    <div className="space-y-2">
      {roles.map((r) => (
        <label key={r.name} className="flex items-start gap-3 rounded-md border p-3 cursor-pointer hover:bg-accent">
          <Checkbox
            checked={value.includes(r.name)}
            onCheckedChange={() => onChange(r.name)}
            className="mt-0.5"
          />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium capitalize">{r.name.replace(/_/g, ' ')}</span>
              <Badge variant="secondary">{r.permission_count} permissions</Badge>
              {r.is_owner && <Badge variant="destructive">Full control</Badge>}
            </div>
            <p className="text-xs text-muted-foreground">{r.description}</p>
          </div>
        </label>
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Team Access</h1>
          <p className="text-sm text-muted-foreground">
            The people who can sign into this Admin Portal — you and your staff.
            These are <span className="font-medium">not</span> tenant users: an
            account here grants no access to any customer's data by default, and
            adding someone here is the only way to give them the portal.
          </p>
        </div>
        <PermissionGate permission={WRITE}>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> Add Team Member
          </Button>
        </PermissionGate>
      </div>

      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">All Team Members ({total})</CardTitle>
          <CardDescription>
            Disabling an operator revokes their live sessions immediately, as does
            changing their roles or resetting their password.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <div className="relative min-w-[240px] flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Search email or name…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              />
            </div>
            <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[190px]"><SelectValue placeholder="All roles" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_FILTER}>All roles</SelectItem>
                {roles.map((r) => (
                  <SelectItem key={r.name} value={r.name} className="capitalize">
                    {r.name.replace(/_/g, ' ')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={activeFilter} onValueChange={(v) => { setActiveFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_FILTER}>Any status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="disabled">Disabled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : operators.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              No team members match these filters.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Operator</TableHead>
                  <TableHead>Roles</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last sign-in</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {operators.map((op) => (
                  <TableRow key={op.id}>
                    <TableCell>
                      <div className="font-medium">{op.first_name} {op.last_name}</div>
                      <div className="text-xs text-muted-foreground">{op.email}</div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(op.roles || []).map((r) => (
                          <Badge key={r} variant="secondary" className="capitalize">
                            {r.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      {op.is_active
                        ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Active</Badge>
                        : <Badge variant="outline">Disabled</Badge>}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {op.last_login_at ? new Date(op.last_login_at).toLocaleString() : 'Never'}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <PermissionGate permission={WRITE}>
                          <Button variant="ghost" size="sm" onClick={() => setEditing({ ...op, roles: [...(op.roles || [])] })}>
                            <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
                          </Button>
                        </PermissionGate>
                        <PermissionGate permission={WRITE}>
                          <Button variant="ghost" size="sm" onClick={() => setResetting(op)}>
                            <KeyRound className="mr-1 h-3.5 w-3.5" /> Password
                          </Button>
                        </PermissionGate>
                        <PermissionGate permission={WRITE}>
                          <Button variant="ghost" size="sm" disabled={saving} onClick={() => revokeSessions(op)}>
                            <LogOut className="mr-1 h-3.5 w-3.5" /> Sign out
                          </Button>
                        </PermissionGate>
                        <PermissionGate permission={WRITE}>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={saving || (op.is_active && isSelf(op))}
                            title={op.is_active && isSelf(op)
                              ? 'You cannot disable your own operator account.'
                              : undefined}
                            onClick={() => toggleActive(op)}
                          >
                            {op.is_active
                              ? <><UserX className="mr-1 h-3.5 w-3.5" /> Disable</>
                              : <><UserCheck className="mr-1 h-3.5 w-3.5" /> Enable</>}
                          </Button>
                        </PermissionGate>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {pages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <span className="text-sm text-muted-foreground">Page {page + 1} of {pages}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                  Previous
                </Button>
                <Button variant="outline" size="sm" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
            <DialogDescription>
              They will sign in at this portal with the password you set here.
              Passwords must be at least 12 characters.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>First name</Label>
                <Input value={draft.first_name} onChange={(e) => setDraft({ ...draft, first_name: e.target.value })} /></div>
              <div><Label>Last name</Label>
                <Input value={draft.last_name} onChange={(e) => setDraft({ ...draft, last_name: e.target.value })} /></div>
            </div>
            <div><Label>Email</Label>
              <Input type="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} /></div>
            <div><Label>Password</Label>
              <Input type="password" value={draft.password} onChange={(e) => setDraft({ ...draft, password: e.target.value })} /></div>
            <div>
              <Label className="mb-2 block">Roles</Label>
              <RolePicker value={draft.roles} onChange={(r) => setDraft(toggleRole(draft, r))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={createOperator} disabled={saving || draft.roles.length === 0}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add Team Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Team Member</DialogTitle>
            <DialogDescription>
              Changing roles signs this operator out everywhere, so the new
              permissions apply immediately rather than at their next sign-in.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>First name</Label>
                  <Input value={editing.first_name || ''} onChange={(e) => setEditing({ ...editing, first_name: e.target.value })} /></div>
                <div><Label>Last name</Label>
                  <Input value={editing.last_name || ''} onChange={(e) => setEditing({ ...editing, last_name: e.target.value })} /></div>
              </div>
              <div className="text-sm text-muted-foreground">{editing.email}</div>
              <div>
                <Label className="mb-2 block">Roles</Label>
                <RolePicker value={editing.roles} onChange={(r) => setEditing(toggleRole(editing, r))} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={saving || !editing?.roles?.length}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password */}
      <Dialog open={!!resetting} onOpenChange={(o) => !o && setResetting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set a new password</DialogTitle>
            <DialogDescription>
              For {resetting?.email}. This signs them out everywhere — if the old
              password is the reason you are resetting it, leaving their sessions
              alive would defeat the reset.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>New password</Label>
            <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            <p className="mt-1 text-xs text-muted-foreground">At least 12 characters.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetting(null)}>Cancel</Button>
            <Button onClick={resetPassword} disabled={saving || newPassword.length < 12}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <ShieldCheck className="mr-2 h-4 w-4" /> Set password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OperatorsPage;
